/**
 * Utilitaire de gestion des versions avec build number automatique
 */

export interface VersionInfo {
  full: string;           // "1.1.0.123"
  display: string;        // "v1.1.0 (build 123)"
  buildNumber: string;    // "123"
  timestamp: number;      // Timestamp de build
  environment: string;    // "development" | "production"
}

/**
 * Récupère les informations de version depuis les variables d'environnement
 */
export function getVersionInfo(): VersionInfo {
  const fullVersion = process.env.REACT_APP_VERSION || '1.1.0.dev';
  const buildNumber = process.env.REACT_APP_BUILD_NUMBER || 'dev';
  const timestamp = parseInt(process.env.REACT_APP_BUILD_TIMESTAMP || Date.now().toString());
  const environment = process.env.NODE_ENV || 'development';
  
  // Version d'affichage conviviale
  let displayVersion: string;
  
  if (environment === 'development') {
    displayVersion = `v${fullVersion} (dev)`;
  } else {
    // Extraire version base pour l'affichage (1.1.0.123 → v1.1.0)
    const baseVersion = fullVersion.split('.').slice(0, 3).join('.');
    displayVersion = `v${baseVersion} (build ${buildNumber})`;
  }
  
  return {
    full: fullVersion,
    display: displayVersion,
    buildNumber,
    timestamp,
    environment
  };
}

/**
 * Retourne uniquement la version complète (pour compatibilité)
 */
export function getFullVersion(): string {
  return getVersionInfo().full;
}

/**
 * Retourne la version d'affichage conviviale
 */
export function getDisplayVersion(): string {
  return getVersionInfo().display;
}

/**
 * Compare deux versions pour déterminer si newVersion est plus récente
 */
export function isNewerVersion(currentVersion: string, newVersion: string): boolean {
  // Si les versions sont identiques
  if (currentVersion === newVersion) {
    return false;
  }
  
  // Fonction pour parser une version en tableau de nombres
  const parseVersion = (version: string): number[] => {
    // Nettoyer la version (enlever v, espaces, etc.)
    const cleaned = version.replace(/^v/, '').trim();
    const parts = cleaned.split('.');
    
    return [
      parseInt(parts[0]) || 0, // major
      parseInt(parts[1]) || 0, // minor  
      parseInt(parts[2]) || 0, // patch
      parseInt(parts[3]) || 0  // build
    ];
  };
  
  const current = parseVersion(currentVersion);
  const newer = parseVersion(newVersion);
  
  // Comparer partie par partie
  for (let i = 0; i < 4; i++) {
    if (newer[i] > current[i]) return true;
    if (newer[i] < current[i]) return false;
  }
  
  return false; // Versions identiques
}

/**
 * Affiche les informations de version dans la console (debug)
 */
export function logVersionInfo(): void {
  const info = getVersionInfo();
  
  console.group('📦 Version Information');
  console.log('Full version:', info.full);
  console.log('Display version:', info.display);
  console.log('Build number:', info.buildNumber);
  console.log('Environment:', info.environment);
  console.log('Build date:', new Date(info.timestamp).toISOString());
  console.groupEnd();
}

// En développement, afficher automatiquement les infos de version
if (process.env.NODE_ENV === 'development') {
  // Attendre que la console soit prête
  setTimeout(() => {
    logVersionInfo();
  }, 1000);
}